import { ComponentType, lazy } from 'react';

/**
 * Wrapper around React.lazy that retries failed imports
 * This helps handle chunk loading errors from stale cached versions
 *
 * @param importFunc - The dynamic import function
 * @param retries - Number of retry attempts (default: 3)
 * @param interval - Time to wait between retries in ms (default: 1000)
 */
export function lazyRetry<T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  retries: number = 3,
  interval: number = 1000
): React.LazyExoticComponent<T> {
  return lazy(() => {
    return new Promise<{ default: T }>((resolve, reject) => {
      // Try to import the module
      const attemptImport = (attemptsLeft: number) => {
        importFunc()
          .then(resolve)
          .catch((error) => {
            // Check if this is a chunk loading error
            const isChunkError =
              error?.name === 'ChunkLoadError' ||
              error?.message?.includes('Loading chunk') ||
              error?.message?.includes('dynamically imported module') ||
              error?.message?.includes('Failed to fetch');

            // If we have retries left and it's a chunk error, retry
            if (attemptsLeft > 0 && isChunkError) {
              setTimeout(() => {
                attemptImport(attemptsLeft - 1);
              }, interval);
            } else {
              // All retries exhausted or non-chunk error - reject and let error boundary handle it
              reject(error);
            }
          });
      };

      attemptImport(retries);
    });
  });
}

/**
 * Alternative: Simple wrapper without retry logic
 */
export function lazyWithoutRetry<T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(() => {
    return importFunc().catch((error) => {
      throw error;
    });
  });
}
